import Reveal, { SectionLabel } from "@/components/Reveal";
import { LineChart, Lock, Send, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const PATHS = [
  {
    icon: LineChart,
    tag: "The Tool",
    title: "The Indicator",
    desc: "The custom-coded TradingView indicator that maps watchzones, liquidity sweeps and confluence directly onto your chart.",
    access: "Lifetime Access Pass",
    cta: "See It Work",
    target: "indicator",
    testid: "paths-card-indicator",
    ctaTestid: "paths-cta-indicator",
  },
  {
    icon: Lock,
    tag: "The Rooms",
    title: "Atlas Exclusive",
    desc: "The members-only rooms: daily blueprints, session coverage, live chart demos and priority Q&A across Gold, indices and FX.",
    access: "Lifetime Access Pass",
    cta: "Look Inside",
    target: "exclusive",
    testid: "paths-card-exclusive",
    ctaTestid: "paths-cta-exclusive",
  },
  {
    icon: Send,
    tag: "The Open Door",
    title: "Free Community",
    desc: "Open to everyone — market discussion, weekly takeaways and a feel for how we work before you spend a penny.",
    access: "Free Forever",
    cta: "Join Free",
    href: "https://t.me/addlist/O9NU4HFXS-cxYzNk",
    testid: "paths-card-community",
    ctaTestid: "paths-cta-community",
  },
];

const Paths = () => (
  <section id="paths" className="relative py-16 lg:py-24" data-testid="paths-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10 lg:mb-14">
          <div>
            <SectionLabel>Where You Fit</SectionLabel>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-slate-50 leading-tight" data-testid="paths-title">
              Three layers.
              <span className="text-gold-gradient"> One goal — your independence.</span>
            </h2>
          </div>
          <p className="text-sm text-slate-500 max-w-xs leading-relaxed">
            Start free, or unlock the full system — the tool and the rooms come together.
          </p>
        </div>
      </Reveal>

      <div className="grid md:grid-cols-3 gap-6">
        {PATHS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.1}>
            <div
              className="h-full flex flex-col border border-line rounded-md bg-card2/40 hover:border-gold/40 transition-colors duration-500 p-6 sm:p-7 group"
              data-testid={p.testid}
            >
              <div className="flex items-center justify-between">
                <span className="w-10 h-10 rounded-sm border border-gold/30 bg-gold/5 flex items-center justify-center">
                  <p.icon size={17} className="text-gold" />
                </span>
                <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-slate-500">{p.tag}</span>
              </div>
              <h3 className="mt-6 font-display font-bold text-xl text-slate-100">{p.title}</h3>
              <p className="mt-2.5 text-sm text-slate-400 leading-relaxed flex-1">{p.desc}</p>
              <div className="mt-6 pt-5 border-t border-line/70 flex items-center justify-between gap-3">
                <span className={`font-mono text-[9px] tracking-[0.2em] uppercase px-2 py-1 rounded-sm border ${
                  p.href ? "border-line text-slate-400" : "border-gold/30 text-gold/90"
                }`}>
                  {p.access}
                </span>
                {p.href ? (
                  <a
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={p.ctaTestid}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:text-gold-bright transition-colors duration-300"
                  >
                    {p.cta}
                    <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                ) : (
                  <button
                    onClick={() => scrollToSection(p.target)}
                    data-testid={p.ctaTestid}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:text-gold-bright transition-colors duration-300"
                  >
                    {p.cta}
                    <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Paths;
